/**
 * Centralized query-key factory.
 *
 * Convention: every feature gets a top-level entry. List queries take an
 * optional filter object so different filter sets are cached separately.
 *
 * Example:
 *   useQuery({ queryKey: queryKeys.employees.list({ search }), queryFn: ... })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.employees.all })
 */
export const queryKeys = {
  employees: {
    all: ['employees'] as const,
    list: (filters?: Record<string, unknown>) => ['employees', 'list', filters ?? {}] as const,
    detail: (id: string | number) => ['employees', 'detail', id] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (filters?: Record<string, unknown>) => ['customers', 'list', filters ?? {}] as const,
    detail: (id: string | number) => ['customers', 'detail', id] as const,
  },
  items: {
    all: ['items'] as const,
    list: (filters?: Record<string, unknown>) => ['items', 'list', filters ?? {}] as const,
    detail: (id: string | number) => ['items', 'detail', id] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    today: () => ['attendance', 'today'] as const,
    list: (filters?: Record<string, unknown>) => ['attendance', 'list', filters ?? {}] as const,
  },
  leaves: {
    all: ['leaves'] as const,
    list: (filters?: Record<string, unknown>) => ['leaves', 'list', filters ?? {}] as const,
    balances: (employeeId: string | number) => ['leaves', 'balances', employeeId] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    my: () => ['dashboard', 'my'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['notifications', 'list', filters ?? {}] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },
  // Add more features here as they migrate to TanStack Query.
} as const
